import { toast } from 'react-toastify';

import { API_SERVICE, type TApiService } from '@/constants/api.constant';
import { useAuthStore } from '@/store';
import type { AuthResponse } from '@/types/auth/auth.response';
import {
	ErrorApiResponse,
	ErrorApiResponseWithData,
	type ErrorResponse
} from '@/types/response';

const toastId = {
	redirectedToLogin: 'redirectedToLogin',
	sessionExpired: 'sessionExpired',
	tokenNotFound: 'tokenNotFound',
	throwError: 'throwError'
};

type PendingRequest = {
	url: string,
	options: RequestInit,
	resolve: (value: Response) => void,
	reject: (reason?: unknown) => void
};

interface FetchConfig {
	getRefreshToken: () => string | null;
	getAccessToken: () => string | null;
	setTokens: (auth: AuthResponse['data']) => void;
	onAuthFailure: (error: Error) => void;
	getRefreshUrl: string;
}

const apiConfig: FetchConfig = {
	getRefreshToken: () => useAuthStore.getState().refresh_token,
	getAccessToken: () => useAuthStore.getState().id_token,
	setTokens: (auth: AuthResponse['data']) => {
		useAuthStore.getState().setStore('id_token', auth.id_token);
		useAuthStore.getState().setStore('refresh_token', auth.refresh_token);
		useAuthStore.getState().setStore('access_token', auth.access_token);
	},
	onAuthFailure: (error: Error) => {
		toast.error(error.message || 'Session Expired', {
			autoClose: 1500,
			onClose: () => {
				useAuthStore.getState().logout();
				toast.warn('Redirected to login', {
					hideProgressBar: true,
					autoClose: 1000,
					toastId: toastId.redirectedToLogin
				});
			},
			toastId: toastId.sessionExpired
		});
	},
	getRefreshUrl: `${API_SERVICE.API}/auth/refresh-token`
};

const setAuthHeader = (options: RequestInit, token: string) => {
	if (options.headers instanceof Headers) {
		options.headers.set('Authorization', `Bearer ${token}`);
	} else if (Array.isArray(options.headers)) {
		options.headers.push(['Authorization', `Bearer ${token}`]);
	} else {
		options.headers = {
			...options.headers,
			Authorization: `Bearer ${token}`
		};
	}
};

const createDeduplicatedFetch = (config: FetchConfig) => {
	const inFlightRequests = new Map<string, Promise<Response>>();
	let isRefreshing = false;
	let refreshPromise: Promise<string | null> | null = null;
	let pendingRequests: PendingRequest[] = [];

	const createStableKey = (url: string, options: RequestInit): string => {
		const replacer = (_key: string, value: unknown) => {
			if (value && typeof value === 'object' && !Array.isArray(value)) {
				return Object.keys(value)
					.sort((a, b) => a.localeCompare(b))
					.reduce(
						(sorted, key) => {
							sorted[key] = (value as Record<string, unknown>)[key];
							return sorted;
						},
						{} as Record<string, unknown>
					);
			}
			return value;
		};
		return `${url}-${JSON.stringify(options, replacer)}`;
	};

	const refreshTokenWithRetry = async (retries = 1): Promise<string | null> => {
		let attempt = 0;

		while (attempt < retries) {
			try {
				const refreshToken = config.getRefreshToken();
				const refreshedTokenResponse = await fetch(apiConfig.getRefreshUrl, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json'
					},
					body: JSON.stringify({ refresh_token: refreshToken })
				});

				if (!refreshedTokenResponse.ok) {
					throw new Error('Failed to retrieve new session');
				}

				const tokens = (await refreshedTokenResponse.json()) as AuthResponse;
				config.setTokens(tokens.data);
				return tokens.data.id_token;
			} catch (error) {
				attempt += 1;
				if (attempt >= retries) {
					if (error instanceof Error) {
						config.onAuthFailure(error);
					} else {
						config.onAuthFailure(new Error(String(error)));
					}
					return null;
				}
			}
		}
		return null;
	};

	const deduplicatedFetch = async (
		url: string,
		options: RequestInit = {}
	): Promise<Response> => {
		const token = config.getAccessToken();

		const hasAuth =
			options.headers instanceof Headers
				? options.headers.has('Authorization')
				: (options.headers as Record<string, string>)?.['Authorization'];

		if (token && !hasAuth) {
			setAuthHeader(options, token);
		}

		const requestKey = createStableKey(url, options);

		const existingInFlightRequests = inFlightRequests.get(requestKey);
		if (existingInFlightRequests) {
			return existingInFlightRequests.then((res) => res.clone());
		}

		if (isRefreshing) {
			return new Promise<Response>((resolve, reject) => {
				pendingRequests.push({ url, options, resolve, reject });
			});
		}

		const fetchPromise = fetch(url, options)
			.then(async (response) => {
				if (response.status === 401 && !url.includes('auth')) {
					if (!isRefreshing) {
						const currentRefreshToken = config.getRefreshToken();

						if (!currentRefreshToken) {
							const error = new Error('Session expired: No refresh token');
							config.onAuthFailure(error);
							throw error;
						}

						isRefreshing = true;
						refreshPromise = refreshTokenWithRetry();
					}

					try {
						const newToken = await refreshPromise;

						if (!newToken) {
							const error = new Error('Failed to retrieve new session');
							pendingRequests.forEach((p) => p.reject(error));
							pendingRequests = [];
							throw error;
						}

						const pendingPromises = pendingRequests.map((p) => {
							setAuthHeader(p.options, newToken);
							return deduplicatedFetch(p.url, p.options)
								.then(p.resolve)
								.catch(p.reject);
						});

						await Promise.allSettled(pendingPromises);

						setAuthHeader(options, newToken);

						return fetch(url, options);
					} finally {
						isRefreshing = false;
						refreshPromise = null;
						pendingRequests = [];
					}
				}

				return response;
			})
			.finally(() => {
				inFlightRequests.delete(requestKey);
			});

		inFlightRequests.set(requestKey, fetchPromise);
		return fetchPromise.then((res) => res.clone());
	};

	return deduplicatedFetch;
};

const customFetch = createDeduplicatedFetch(apiConfig);

const handleTokenNotFound = () => {
	toast.error('Session not found', {
		hideProgressBar: true,
		onClose: () => {
			useAuthStore.getState().logout();

			toast.warn('Redirected to login', {
				hideProgressBar: true,
				autoClose: 1000,
				toastId: toastId.redirectedToLogin
			});
		},
		toastId: toastId.tokenNotFound
	});
};

const getToken = (path?: string): string | null => {
	if (!path) return null;
	const token = useAuthStore.getState().id_token;

	if (!token) {
		handleTokenNotFound();
		return null;
	}

	return token;
};

export interface FetchType {
	path: string;
	params?: Record<string, unknown>;
	payload?: Record<string, unknown> | FormData;
	headers?: Record<string, unknown>;
}

interface Url {
	path: string;
	service: TApiService;
	query?: Record<string, unknown>;
}

const createSearchParams = (
	query: Record<string, unknown>
): URLSearchParams => {
	const params = new URLSearchParams();

	for (const [key, value] of Object.entries(query)) {
		if (
			value === null ||
			value === undefined ||
			(typeof value === 'string' && !value)
		) {
			continue;
		}

		if (Array.isArray(value)) {
			value.forEach((item) => {
				params.append(key, String(item));
			});
		} else {
			params.append(key, value as string);
		}
	}

	return params;
};

export const GET = async <T>(
	url: Url,
	options: RequestInit = {}
): Promise<T> => {
	let token: string | null = null;

	if (!['auth'].some((i) => url.path.includes(i))) {
		token = getToken(url.path);
	}

	const headers: Headers = new Headers();

	if (token) {
		headers.append('Authorization', `Bearer ${token}`);
	}

	headers.append('Content-Type', 'application/json');
	headers.append('Accept', 'application/json');

	let uri = `${url.service}${url.path}`;

	if (url.query && Object.keys(url.query).length > 0) {
		const searchParams = createSearchParams(url.query);
		const queryString = searchParams.toString();

		if (queryString) {
			uri += `?${queryString}`;
		}
	}

	return new Promise((resolve, reject) => {
		customFetch(uri, {
			headers,
			method: 'GET',
			...options
		})
			.then(async (res) => {
				if (!res.ok) {
					const errorData = (await res.json()) as ErrorResponse;
					if ('data' in errorData) {
						throw new ErrorApiResponseWithData(
							errorData.message,
							res.status,
							errorData.errors,
							errorData.data
						);
					}
					throw new ErrorApiResponse(
						errorData.message,
						res.status,
						errorData.errors
					);
				}

				return res.json();
			})
			.then((response: unknown) => resolve(response as T))
			.catch(reject);
	});
};

export const POST = async <T>(
	url: Url,
	options: StrictOmit<RequestInit, 'body'> & {
		body?: RequestInit['body'] | Record<string, unknown>
	} = {}
): Promise<T> => {
	let token: string | null = null;

	if (!['auth'].some((i) => url.path.includes(i))) {
		token = getToken(url.path);
	}

	const headers: Headers = new Headers();
	if (token) {
		headers.append('Authorization', `Bearer ${token}`);
	}

	const isFormData = options.body instanceof FormData;

	if (!isFormData) {
		headers.append('Content-Type', 'application/json');
	}

	headers.append('Accept', 'application/json');

	let uri = `${url.service}${url.path}`;

	if (url.query && Object.keys(url.query).length > 0) {
		const searchParams = createSearchParams(url.query);
		const queryString = searchParams.toString();

		if (queryString) {
			uri += `?${queryString}`;
		}
	}

	return new Promise((resolve, reject) => {
		customFetch(uri, {
			...options,
			headers,
			method: 'POST',
			body: (isFormData
				? options.body
				: JSON.stringify(options.body)) as RequestInit['body']
		})
			.then(async (res) => {
				if (!res.ok) {
					const errorData = (await res.json()) as ErrorResponse;
					if ('data' in errorData) {
						throw new ErrorApiResponseWithData(
							errorData.message,
							res.status,
							errorData.errors,
							errorData.data
						);
					}
					throw new ErrorApiResponse(
						errorData.message,
						res.status,
						errorData.errors
					);
				}

				return res.json();
			})
			.then((response: unknown) => resolve(response as T))
			.catch(reject);
	});
};

export const PUT = async <T>(
	url: Url,
	options: StrictOmit<RequestInit, 'body'> & {
		body?: RequestInit['body'] | Record<string, unknown>
	} = {}
): Promise<T> => {
	let token: string | null = null;

	if (!['auth'].some((i) => url.path.includes(i))) {
		token = getToken(url.path);
	}

	const headers: Headers = new Headers();
	if (token) {
		headers.append('Authorization', `Bearer ${token}`);
	}

	const isFormData = options.body instanceof FormData;

	if (!isFormData) {
		headers.append('Content-Type', 'application/json');
	}

	headers.append('Accept', 'application/json');

	let uri = `${url.service}${url.path}`;

	if (url.query && Object.keys(url.query).length > 0) {
		const searchParams = createSearchParams(url.query);
		const queryString = searchParams.toString();

		if (queryString) {
			uri += `?${queryString}`;
		}
	}

	return new Promise((resolve, reject) => {
		customFetch(uri, {
			...options,
			headers,
			method: 'PUT',
			body: (isFormData
				? options.body
				: JSON.stringify(options.body)) as RequestInit['body']
		})
			.then(async (res) => {
				if (!res.ok) {
					const errorData = (await res.json()) as ErrorResponse;
					if ('data' in errorData) {
						throw new ErrorApiResponseWithData(
							errorData.message,
							res.status,
							errorData.errors,
							errorData.data
						);
					}
					throw new ErrorApiResponse(
						errorData.message,
						res.status,
						errorData.errors
					);
				}

				return res.json();
			})
			.then((response: unknown) => resolve(response as T))
			.catch(reject);
	});
};

export const DELETE = async <T>(
	url: Url,
	options: StrictOmit<RequestInit, 'body'> & {
		body?: RequestInit['body'] | Record<string, unknown>
	} = {}
): Promise<T> => {
	let token: string | null = null;

	if (!['auth'].some((i) => url.path.includes(i))) {
		token = getToken(url.path);
	}

	const headers: Headers = new Headers();
	if (token) {
		headers.append('Authorization', `Bearer ${token}`);
	}

	const isFormData = options.body instanceof FormData;

	if (!isFormData) {
		headers.append('Content-Type', 'application/json');
	}

	headers.append('Accept', 'application/json');

	let uri = `${url.service}${url.path}`;

	if (url.query && Object.keys(url.query).length > 0) {
		const searchParams = createSearchParams(url.query);
		const queryString = searchParams.toString();

		if (queryString) {
			uri += `?${queryString}`;
		}
	}

	return new Promise((resolve, reject) => {
		customFetch(uri, {
			...options,
			headers,
			method: 'DELETE',
			body: (isFormData
				? options.body
				: JSON.stringify(options.body)) as RequestInit['body']
		})
			.then(async (res) => {
				if (!res.ok) {
					const errorData = (await res.json()) as ErrorResponse;
					if ('data' in errorData) {
						throw new ErrorApiResponseWithData(
							errorData.message,
							res.status,
							errorData.errors,
							errorData.data
						);
					}
					throw new ErrorApiResponse(
						errorData.message,
						res.status,
						errorData.errors
					);
				}

				return res.json();
			})
			.then((response: unknown) => resolve(response as T))
			.catch(reject);
	});
};

interface UploadOptions {
	sasUrl: string;
	file: File;
	onProgress?: (percentage: number) => void;
}

export function UPLOAD_FILE<T>({
	sasUrl,
	file,
	onProgress
}: UploadOptions): Promise<T> {
	return new Promise((resolve, reject) => {
		const xhr = new XMLHttpRequest();

		xhr.open('PUT', sasUrl);

		xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
		xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

		if (xhr.upload && onProgress) {
			xhr.upload.onprogress = (event: ProgressEvent) => {
				if (event.lengthComputable) {
					const percentComplete = Math.round((event.loaded / event.total) * 100);
					onProgress(percentComplete);
				}
			};
		}

		xhr.onload = () => {
			if (xhr.status >= 200 && xhr.status < 300) {
				resolve({ success: true } as T);
			} else {
				reject(
					new Error(`Upload failed with status: ${xhr.status} - ${xhr.statusText}`)
				);
			}
		};

		xhr.onerror = () => {
			reject(new Error('Network error during upload'));
		};

		xhr.send(file);
	});
}

export const GET_FILE = async <T>(
	url: Url,
	options: RequestInit = {}
): Promise<T> => {
	let token: string | null = null;

	if (!['auth'].some((i) => url.path.includes(i))) {
		token = getToken(url.path);
	}

	const headers: Headers = new Headers();

	if (token) {
		headers.append('Authorization', `Bearer ${token}`);
	}

	let uri = `${url.service}${url.path}`;

	if (url.query && Object.keys(url.query).length > 0) {
		const searchParams = createSearchParams(url.query);
		const queryString = searchParams.toString();

		if (queryString) {
			uri += `?${queryString}`;
		}
	}

	return new Promise((resolve, reject) => {
		customFetch(uri, {
			headers,
			method: 'GET',
			...options
		})
			.then(async (res) => {
				if (!res.ok) {
					const errorData = (await res.json()) as ErrorResponse;
					if ('data' in errorData) {
						throw new ErrorApiResponseWithData(
							errorData.message,
							res.status,
							errorData.errors,
							errorData.data
						);
					}
					throw new ErrorApiResponse(
						errorData.message,
						res.status,
						errorData.errors
					);
				}

				return res.blob();
			})
			.then((response: unknown) => resolve(response as T))
			.catch(reject);
	});
};
